/**
 * kc-product-list-cd — client side of blocks/kc-product-list-cd.liquid.
 *
 * This used to be two inline <script> blocks emitted by every instance of the
 * block (≈33 KB each; the homepage has two), with every function and global
 * suffixed by the block id (switchCarousel_<id>, kcTabCache_<id>, …). It is now
 * one cacheable module: one KcProductListCd instance per block root
 * (`.kc-product-list-block[data-cd-block]`), same behaviour.
 *
 * Loaded with <script type="module">, so it is evaluated ONCE per page however
 * many blocks emit the tag, after theme.js has defined <scroll-carousel>.
 *
 * What it does (see CLAUDE.md, "kc-product-list-cd on-demand tabs"):
 *  - Tabs (data-cd-mode="tabs"): only the first collection is server-rendered.
 *    The other tabs ship an empty track and are fetched on demand from
 *      /collections/<handle>?view=cdtab&section_id=<containing section id>
 *    (Section Rendering API re-rendering this same section in fragment mode).
 *  - Promo cards (kc-product-list-cd-promo-card children) are moved into a
 *    hidden "blueprint" <template> and cloned into the right slot of each tab.
 *  - --cd-track-height pins the same height on every tab so switching never
 *    moves the content below the carousel.
 */

const REGISTRY = new WeakMap();

function routeRoot() {
  // Shopify.routes is absent in a few contexts and carries the locale prefix on
  // multi-language storefronts (same defensive form as kc-warranty.js).
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
}

function whenIdle(callback, timeout, fallbackDelay) {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(callback, { timeout: timeout });
  } else {
    setTimeout(callback, fallbackDelay);
  }
}

function afterLoad(callback) {
  if (document.readyState === 'complete') {
    callback();
  } else {
    window.addEventListener('load', callback, { once: true });
  }
}

class KcProductListCd {
  constructor(root) {
    this.root = root;
    this.id = root.dataset.cdBlock;
    this.tabs = root.dataset.cdMode === 'tabs';
    this.wrapper = root.querySelector('.multi-collection-carousel-wrapper');
    // handle -> Promise<track|null>. The PROMISE is cached, not its result, so an
    // intent prefetch followed by a click never issues a second request.
    this.cache = new Map();
    // Id of the tab the visitor asked for last (out-of-order resolution guard).
    this.pendingTab = null;
  }

  init() {
    this.distributePromos();
    this.markActiveCarouselsAsHandled();

    if (!this.tabs || !this.wrapper) return;

    // Tab clicks (was an inline onclick="switchCarousel_<id>(…)" on each button).
    this.root.addEventListener('click', (event) => {
      const button = event.target && event.target.closest && event.target.closest('.collection-tab');
      if (!button || !this.root.contains(button)) return;
      const targetId = button.dataset.carouselTarget;
      if (targetId) this.switchTo(button, targetId);
    });

    this.setupIntentPrefetch();
    this.preloadInitialActiveTab();
    this.prefetchInactiveTabsIdle();
    this.initTrackHeight();
  }

  // The section id is read from the DOM rather than Liquid: a theme block has no
  // reliable `section` object in scope, and `.shopify-section` is the wrapper both
  // on the storefront and in the editor.
  sectionId() {
    const section = this.wrapper && this.wrapper.closest('.shopify-section');
    return section ? section.id.replace(/^shopify-section-/, '') : null;
  }

  // Returns a Promise<Element|null> (the fetched track).
  fetchTab(handle) {
    if (!handle) return Promise.resolve(null);
    if (this.cache.has(handle)) return this.cache.get(handle);

    const sectionId = this.sectionId();
    if (!sectionId) return Promise.resolve(null);

    const url =
      routeRoot() + 'collections/' + encodeURIComponent(handle) + '?view=cdtab&section_id=' + encodeURIComponent(sectionId);

    const promise = fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((response) => {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then((html) => {
        // createContextualFragment parses without running scripts and without the
        // cost of a full DOMParser document.
        const fragment = document.createRange().createContextualFragment(html);
        // Scope by block id first: a section can hold more than one of these
        // blocks, and in fragment mode every one of them emits a track for the
        // requested collection.
        const track =
          fragment.querySelector('[data-tab-index][data-cd-block="' + this.id + '"]') ||
          fragment.querySelector('[data-tab-index]');
        return track || null;
      })
      .catch((error) => {
        // Drop the rejected promise so a later click can retry.
        this.cache.delete(handle);
        console.warn('[kc-product-list-cd] tab fetch failed for "' + handle + '"', error);
        return null;
      });

    this.cache.set(handle, promise);
    return promise;
  }

  // Moves the fetched cards into the live track. Idempotent.
  hydrateTab(container, fetchedTrack) {
    if (!container || container.dataset.loaded === 'true') return false;
    const track = container.querySelector('.collection-carousel-track');
    if (!track || !fetchedTrack) return false;
    track.replaceChildren(...fetchedTrack.childNodes);
    container.dataset.loaded = 'true';
    return true;
  }

  // Everything that has to happen AFTER a tab's cards are in the DOM. Shared by
  // the synchronous (cached) and asynchronous (cold fetch) paths.
  finishTabSwitch(targetContainer) {
    // Promo cards for this tab, cloned from the block's blueprint.
    this.placePromos(targetContainer);

    // Fade-in animation class, cleared after the animation (240ms + buffer).
    targetContainer.classList.add('tab-just-activated');
    setTimeout(() => {
      targetContainer.classList.remove('tab-just-activated');
    }, 320);

    // The cards are emitted loading="lazy"; the ones in the carousel peek area
    // (≈20px visible) often never trigger the lazy observer and would show as
    // empty image slots. The visitor asked for this tab: load its images now.
    this.eagerLoadImages(targetContainer);

    // An inactive tab's <scroll-carousel> was connected while its track was
    // empty, so BaseCarousel skipped its control:* listeners (they are gated by
    // items.length > 1) and cached an empty item list. Invalidate the caches,
    // reset the ScrollArea edge flags and install the missing listeners once.
    const carouselEl = targetContainer.querySelector('scroll-carousel');
    if (carouselEl) {
      carouselEl._items = null;
      carouselEl._selectedIndex = null;
      const scrollArea = carouselEl._scrollArea;
      if (scrollArea) {
        scrollArea._lastScrollPosition = undefined;
        scrollArea._allowTriggerNearingStartEvent = false;
        scrollArea._allowTriggerLeavingStartEvent = true;
        scrollArea._allowTriggerNearingEndEvent = true;
        scrollArea._allowTriggerLeavingEndEvent = false;
      }

      if (!carouselEl._kcControlListenersInstalled) {
        const freshItems = carouselEl.items; // forces a re-query
        if (freshItems && freshItems.length > 1) {
          carouselEl._kcControlListenersInstalled = true;
          carouselEl.addEventListener('control:prev', carouselEl.previous);
          carouselEl.addEventListener('control:next', carouselEl.next);
          carouselEl.addEventListener('control:select', (event) => {
            carouselEl.select(event.detail.index);
          });

          // Same end state BaseCarousel.connectedCallback would have produced.
          if (carouselEl.selectedSlide) {
            carouselEl.selectedSlide.classList.add('is-selected');
          }
          if (typeof carouselEl._dispatchEvent === 'function') {
            carouselEl._dispatchEvent('carousel:select', carouselEl.selectedIndex);
          }
        }
      }
    }

    const carousel = targetContainer.querySelector('.collection-carousel');
    if (carousel) {
      carousel.scrollTo({ left: 0, behavior: 'instant' });
      // Force a scroll-driven recompute on the next frame so the edge events and
      // the <scroll-progress> bar use the now-correct widths.
      requestAnimationFrame(() => {
        carousel.dispatchEvent(new Event('scroll'));
      });
    }
  }

  // Visibility swap only.
  activateTab(button, targetContainer) {
    const tabList = button && button.closest && button.closest('.collection-tabs');
    if (tabList) {
      tabList.querySelectorAll('.collection-tab').forEach((tab) => {
        tab.classList.remove('active');
      });
      button.classList.add('active');
    }
    this.wrapper.querySelectorAll('.collection-carousel-container').forEach((container) => {
      container.classList.remove('active');
    });
    targetContainer.classList.add('active');
  }

  // Last-resort UI when the fragment request fails (offline, 5xx): a real link to
  // the collection instead of an empty tab.
  renderTabError(targetContainer, handle, button) {
    const track = targetContainer.querySelector('.collection-carousel-track');
    if (!track || track.children.length > 0) return;
    const link = document.createElement('a');
    link.className = 'kc-cd-tab-error';
    link.href = routeRoot() + 'collections/' + handle;
    // Reuse the tab's own label so there is no new string to translate.
    link.textContent = (button && button.textContent && button.textContent.trim()) || handle;
    track.replaceChildren(link);
  }

  switchTo(button, targetId) {
    const targetContainer = document.getElementById(targetId);
    if (!targetContainer || !this.wrapper.contains(targetContainer)) return;

    // Already populated (the initially-active tab, or one visited before).
    if (targetContainer.dataset.loaded === 'true') {
      this.activateTab(button, targetContainer);
      this.finishTabSwitch(targetContainer);
      return;
    }

    const handle = targetContainer.dataset.collectionHandle;
    // "Warm" = idle/intent prefetch already requested this tab: hydration lands
    // within a few ms, so keep the "hydrate BEFORE showing" order.
    const warm = this.cache.has(handle);

    if (!warm) {
      // Cold: show the tab immediately so the click registers. The track is empty
      // for a moment, but --cd-track-height already reserves the exact height.
      this.activateTab(button, targetContainer);
      targetContainer.classList.add('is-tab-loading');
    }

    // If the visitor clicks tab A and then tab B before A settles, A must still
    // hydrate (the cards are paid for) but must NOT steal the active state back.
    this.pendingTab = targetId;

    this.fetchTab(handle).then((fetchedTrack) => {
      targetContainer.classList.remove('is-tab-loading');
      this.hydrateTab(targetContainer, fetchedTrack);
      if (this.pendingTab !== targetId) return;
      if (!fetchedTrack) this.renderTabError(targetContainer, handle, button);
      if (warm) this.activateTab(button, targetContainer);
      this.finishTabSwitch(targetContainer);
    });
  }

  eagerLoadImages(container) {
    if (!container) return;
    // fetchpriority stays "auto": these are not LCP candidates.
    container.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
    container.querySelectorAll('video[preload="none"]').forEach((video) => {
      video.preload = 'metadata';
    });
  }

  // The FIRST tab's peek-area cards, promoted during idle time after `load` so
  // they do not compete with the page's LCP image.
  preloadInitialActiveTab() {
    afterLoad(() => {
      whenIdle(
        () => {
          if (!this.root.isConnected) return;
          const initialActive = this.wrapper.querySelector('.collection-carousel-container.active');
          if (initialActive) this.eagerLoadImages(initialActive);
        },
        2000,
        100
      );
    });
  }

  // Fetch a tab's markup (shared promise cache) and warm its images with
  // detached Image objects, so hydration paints immediately.
  prefetchTab(container) {
    if (!container || container.dataset.prefetched === 'true' || container.dataset.loaded === 'true') return;
    container.dataset.prefetched = 'true';
    this.fetchTab(container.dataset.collectionHandle).then((fetchedTrack) => {
      if (!fetchedTrack) return;
      fetchedTrack.querySelectorAll('img').forEach((sourceImage) => {
        const image = new Image();
        // sizes/srcset BEFORE src so the responsive descriptors pick the URL.
        if (sourceImage.sizes) image.sizes = sourceImage.sizes;
        if (sourceImage.srcset) image.srcset = sourceImage.srcset;
        if (sourceImage.src) image.src = sourceImage.src;
        if (sourceImage.crossOrigin) image.crossOrigin = sourceImage.crossOrigin;
      });
    });
  }

  // Idle prefetch: after `load`, one inactive tab per idle slot.
  // Skipped on Save-Data and on slow-2g / 2g / 3g connections.
  prefetchInactiveTabsIdle() {
    const connection = navigator.connection;
    if (connection) {
      if (connection.saveData) return;
      const type = connection.effectiveType;
      if (type === 'slow-2g' || type === '2g' || type === '3g') return;
    }

    afterLoad(() => {
      whenIdle(
        () => {
          if (!this.root.isConnected) return;
          const inactive = this.wrapper.querySelectorAll('.collection-carousel-container:not(.active)');
          let index = 0;
          const next = () => {
            if (index >= inactive.length) return;
            this.prefetchTab(inactive[index]);
            index++;
            whenIdle(next, 5000, 250);
          };
          next();
        },
        3000,
        500
      );
    });
  }

  // Intent prefetch: hovering / focusing / touching a tab jumps the queue.
  // Skipped on Save-Data only (the visitor explicitly showed intent).
  setupIntentPrefetch() {
    if (navigator.connection && navigator.connection.saveData) return;

    const handleIntent = (event) => {
      const button = event.target && event.target.closest && event.target.closest('.collection-tab');
      if (!button || !this.root.contains(button)) return;
      const targetId = button.dataset.carouselTarget;
      if (!targetId) return;
      const container = document.getElementById(targetId);
      if (container) this.prefetchTab(container);
    };

    this.root.addEventListener('mouseover', handleIntent);
    this.root.addEventListener('focusin', handleIntent);
    this.root.addEventListener('touchstart', handleIntent, { passive: true });
  }

  // Measure the active tab's track once the page has loaded and pin
  // --cd-track-height on the wrapper (never shrinks; ResizeObserver keeps it in
  // sync across breakpoints).
  initTrackHeight() {
    const wrapper = this.wrapper;

    const measure = () => {
      const activeTrack = wrapper.querySelector('.collection-carousel-container.active .collection-carousel-track');
      if (!activeTrack) return;
      const height = activeTrack.offsetHeight;
      if (height > 0) {
        const previous = parseInt(wrapper.style.getPropertyValue('--cd-track-height')) || 0;
        if (height > previous) {
          wrapper.style.setProperty('--cd-track-height', height + 'px');
        }
      }
    };

    const bind = () => {
      if (!this.root.isConnected) return;
      measure();
      if (typeof ResizeObserver === 'function') {
        const activeTrack = wrapper.querySelector('.collection-carousel-container.active .collection-carousel-track');
        if (activeTrack) new ResizeObserver(measure).observe(activeTrack);
      } else {
        window.addEventListener('resize', measure, { passive: true });
      }
    };

    afterLoad(() => {
      whenIdle(bind, 1500, 50);
    });
  }

  // ---------------------------------------------------------------------------
  // Promo cards — "blueprint of truth"
  // ---------------------------------------------------------------------------
  // Merchant-added kc-product-list-cd-promo-card blocks are rendered outside the
  // carousel (hidden by CSS). They are MOVED once into a hidden
  // <template data-promo-blueprint> at the block root (moving keeps the editor
  // binding on the originals); every tab then receives clones at the configured
  // slot, on first activation. Idempotent per track (data-promos-placed).
  distributePromos() {
    const block = this.root;

    // Originals that are NOT inside a track. Empty on a re-run for an already
    // processed block (they live in the blueprint by then).
    const promos = Array.prototype.filter.call(
      block.querySelectorAll('.kc-product-card[data-promo-position]'),
      (card) => !card.closest('.collection-carousel-track')
    );

    const existingBlueprint = block.querySelector(':scope > template[data-promo-blueprint]');

    // Nothing to do, or already processed.
    if (promos.length === 0) return;

    // Fresh originals (first render, or an editor re-render): rebuild.
    if (existingBlueprint) existingBlueprint.remove();

    block.querySelectorAll('.collection-carousel-track').forEach((track) => {
      delete track.dataset.promosPlaced;
      track.querySelectorAll('.kc-product-card[data-promo-position][data-promo-clone]').forEach((node) => {
        node.remove();
      });
    });

    const blueprint = document.createElement('template');
    blueprint.setAttribute('data-promo-blueprint', '');
    promos.forEach((promo) => {
      blueprint.content.appendChild(promo);
    });
    block.appendChild(blueprint);

    // Place into the currently-active tab right away.
    const activeContainer =
      block.querySelector('.collection-carousel-container.active') || block.querySelector('.collection-carousel-container');
    if (activeContainer) this.placePromos(activeContainer);
  }

  placePromos(container) {
    if (!container) return;
    const track = container.querySelector('.collection-carousel-track');
    if (!track) return;
    if (track.dataset.promosPlaced === 'true') return;

    const blueprint = this.root.querySelector('template[data-promo-blueprint]');
    if (!blueprint || !('content' in blueprint)) return;

    const trackTabIndex = parseInt(track.getAttribute('data-tab-index') || '0', 10);

    // Product cards at ANY depth (Shopify may wrap each rendered block in an
    // editor overlay in design mode); insertion still happens among the track's
    // direct children.
    const productCards = Array.from(track.querySelectorAll('.kc-product-card:not([data-promo-position])'));
    const promos = Array.from(blueprint.content.querySelectorAll('.kc-product-card[data-promo-position]'));

    promos.forEach((promo) => {
      const position = parseInt(promo.getAttribute('data-promo-position') || '1', 10);
      const tabFilter = parseInt(promo.getAttribute('data-promo-tab') || '-1', 10);
      if (tabFilter !== -1 && tabFilter !== trackTabIndex) return;

      const clone = promo.cloneNode(true);
      // Marked so a later rebuild can remove it (the blueprint promo stays).
      clone.setAttribute('data-promo-clone', '');

      const insertIndex = Math.min(Math.max(position - 1, 0), productCards.length);
      let insertBefore = productCards[insertIndex] || null;
      if (insertBefore) {
        while (insertBefore && insertBefore.parentNode !== track) {
          insertBefore = insertBefore.parentNode;
          if (!insertBefore || insertBefore === document) {
            insertBefore = null;
            break;
          }
        }
      }
      if (insertBefore && insertBefore.parentNode === track) {
        track.insertBefore(clone, insertBefore);
      } else {
        track.appendChild(clone);
      }
    });

    track.dataset.promosPlaced = 'true';
  }

  // Carousels that had more than one card when they were connected already got
  // their control:* listeners from BaseCarousel: flag them so finishTabSwitch()
  // does not install duplicates when the visitor returns to that tab.
  markActiveCarouselsAsHandled() {
    this.root.querySelectorAll('scroll-carousel').forEach((carousel) => {
      if (carousel.items && carousel.items.length > 1) {
        carousel._kcControlListenersInstalled = true;
      }
    });
  }
}

function init(root) {
  const existing = REGISTRY.get(root);
  if (existing) {
    // Same node, possibly with fresh promo originals (editor block re-render).
    existing.distributePromos();
    return existing;
  }
  const instance = new KcProductListCd(root);
  REGISTRY.set(root, instance);
  root.dataset.cdReady = '1';
  instance.init();
  return instance;
}

function initAll(scope) {
  (scope || document).querySelectorAll('.kc-product-list-block[data-cd-block]').forEach(init);
}

initAll(document);

// Theme editor: Shopify replaces the section's HTML and scripts inside it do not
// run again (nor does this module re-evaluate), so new block roots are picked up
// from this event.
document.addEventListener('shopify:section:load', (event) => {
  initAll(event.target || document);
});
